import * as React from 'react'
import moment from 'moment'
import { duration } from 'moment'
import type { Moment } from 'moment'
import * as ReactDOM from 'react-dom'

import { ActivityType } from '@/app/constants/activity'
import { ONLY_TIME_FORMAT } from '@/app/constants/date'
import {
  calculateSummary,
  getFormattedCalculation,
} from '@/app/components/timeline/timelineHelper'
import { faClose, FaIcon } from '@/lib/fa'
import { TimelineProps } from '@/modules/drivers/types/activities'

import Timeline from './Timeline'
import { createTimelineScene } from './scene/TimelineScene'

interface PinData {
  time: Moment
  position: number
}

export type RangeSelectionTimelineProps = TimelineProps & {
  rangeSelection?: boolean
  onRangeSelectionChange?: (enabled: boolean) => void
}

export const RangeSelectionTimelineWrapper: React.FC<RangeSelectionTimelineProps> = (
  props,
) => {
  const { rangeSelection, onRangeSelectionChange } = props

  const [pin1, setPin1] = React.useState<PinData | null>(null)
  const [pin2, setPin2] = React.useState<PinData | null>(null)
  const [hoverPosition, setHoverPosition] = React.useState<number | null>(null)
  const [hoverTime, setHoverTime] = React.useState<Moment | null>(null)
  const chartContainerRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!rangeSelection) {
      setPin1(null)
      setPin2(null)
    }
  }, [rangeSelection])

  const scene = React.useMemo(
    () =>
      createTimelineScene({
        startTime: props.startTime,
        days: props.days ?? 1,
        width: props.width,
        height: props.height,
      }),
    [props.startTime?.valueOf?.(), props.days, props.width, props.height],
  )

  const activityEdges = React.useMemo(
    () =>
      ((props.activities as any[]) || []).map((a) => ({
        startTime: a.startTime,
        endTime: a.endTime,
      })),
    [props.activities],
  )

  const resolveAt = (clientX: number): PinData => {
    const rect = chartContainerRef.current!.getBoundingClientRect()
    let x = clientX - rect.left
    if (x < 0) x = 0
    if (x > scene.width) x = scene.width
    const rawTime = scene.xToTime(x)
    const snappedTime = scene.snapToEdges(rawTime, activityEdges)
    return { time: snappedTime, position: scene.timeToX(snappedTime) }
  }

  const handlePinClick = (e: React.MouseEvent) => {
    if (!chartContainerRef.current) return
    const p = resolveAt(e.clientX)
    if (pin1 == null) {
      setPin1(p)
    } else if (pin2 == null) {
      setPin2(p)
    } else {
      setPin1(p)
      setPin2(null)
    }
  }

  const handleHoverMouseMove = (e: React.MouseEvent) => {
    if (!chartContainerRef.current) return
    const p = resolveAt(e.clientX)
    setHoverPosition(p.position)
    setHoverTime(p.time)
  }

  const handleHoverMouseLeave = () => {
    setHoverPosition(null)
    setHoverTime(null)
  }

  const clearPins = () => {
    setPin1(null)
    setPin2(null)
  }

  const hasBothPins = pin1 != null && pin2 != null
  const sortedPins = React.useMemo(() => {
    if (!pin1 || !pin2) return null
    const sorted = [pin1, pin2].sort((a, b) => (a.time.isBefore(b.time) ? -1 : 1))
    return { start: sorted[0], end: sorted[1] }
  }, [pin1, pin2])

  const wholeDayRange = React.useMemo(() => {
    const windowStart = moment(props.startTime)
    if (!windowStart.isValid()) return null
    const windowEnd = windowStart.clone().add(props.days ?? 1, 'days')

    let start: Moment | null = null
    let end: Moment | null = null
    ;((props.activities as any[]) || []).forEach((activity) => {
      const s = moment(activity.startTime)
      if (
        s.isValid() &&
        !s.isBefore(windowStart) &&
        s.isBefore(windowEnd) &&
        (!start || s.isBefore(start))
      ) {
        start = s
      }
      if (!activity.endTime) return
      const e = moment(activity.endTime)
      if (
        e.isValid() &&
        e.isAfter(windowStart) &&
        !e.isAfter(windowEnd) &&
        (!end || e.isAfter(end))
      ) {
        end = e
      }
    })

    const from: Moment = start ?? windowStart.clone()
    const to: Moment = end ?? windowEnd.clone()
    const [rangeStart, rangeEnd] = to.isBefore(from) ? [to, from] : [from, to]
    if (!rangeEnd.isAfter(rangeStart)) return null

    return { start: rangeStart, end: rangeEnd }
  }, [props.activities, props.startTime?.valueOf?.(), props.days])

  const isWholeDaySelected =
    sortedPins != null &&
    wholeDayRange != null &&
    sortedPins.start.time.isSame(wholeDayRange.start) &&
    sortedPins.end.time.isSame(wholeDayRange.end)

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const ctrlOrCmd = e.ctrlKey || e.metaKey
      if (!ctrlOrCmd || !e.shiftKey || e.altKey) return
      const isModifierKey =
        e.key === 'Shift' || e.key === 'Control' || e.key === 'Meta'
      if (!isModifierKey || e.repeat) return

      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) {
        return
      }
      if (!wholeDayRange) return

      if (isWholeDaySelected) {
        clearPins()
        return
      }
      setPin1({
        time: wholeDayRange.start,
        position: scene.timeToX(wholeDayRange.start),
      })
      setPin2({
        time: wholeDayRange.end,
        position: scene.timeToX(wholeDayRange.end),
      })
      if (!rangeSelection) onRangeSelectionChange?.(true)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    wholeDayRange,
    isWholeDaySelected,
    scene,
    rangeSelection,
    onRangeSelectionChange,
  ])

  const selectionSummary = React.useMemo(() => {
    if (!sortedPins) return null

    const dayEnd = moment(props.extraCurrentDate).endOf('day')
    const safeActivities = ((props.activities as any[]) || []).map(
      (activity) => ({
        ...activity,
        endTime: activity.endTime ? activity.endTime : dayEnd.toISOString(),
      }),
    )

    const summary = calculateSummary(
      sortedPins.start.time,
      sortedPins.end.time,
      safeActivities,
    )
    const formatted = getFormattedCalculation(summary)

    const totalMs = sortedPins.end.time.diff(sortedPins.start.time)
    const totalDuration = duration(totalMs)
    const totalHours = Math.floor(totalDuration.asHours())
    const totalMinutes = totalDuration.minutes()

    return {
      ...formatted,
      totalSelected: `${totalHours}h ${totalMinutes
        .toString()
        .padStart(2, '0')}min`,
      period: `${sortedPins.start.time.format(
        ONLY_TIME_FORMAT,
      )} - ${sortedPins.end.time.format(ONLY_TIME_FORMAT)}`,
    }
  }, [sortedPins, props.activities, props.extraCurrentDate])

  const leftOverlayWidth = sortedPins ? Math.max(0, sortedPins.start.position) : 0
  const rightOverlayLeft = sortedPins ? sortedPins.end.position : 0
  const rightOverlayWidth = sortedPins
    ? Math.max(0, scene.width - sortedPins.end.position)
    : 0

  const disableTooltips =
    props.disableTooltips || (rangeSelection && hasBothPins)

  const timeline = React.useMemo(
    () => <Timeline {...({ ...props, disableTooltips } as TimelineProps)} />,
    [props, disableTooltips],
  )

  return (
    <div style={{ position: 'relative', overflow: 'visible'}}>
      <div
        ref={chartContainerRef}
        onClick={rangeSelection ? handlePinClick : undefined}
        onMouseMove={handleHoverMouseMove}
        onMouseLeave={handleHoverMouseLeave}
        style={{
          position: 'relative',
          cursor: rangeSelection ? 'crosshair' : undefined,
        }}
      >
        {timeline}

        {rangeSelection && pin1 && (
          <div
            style={{
              position: 'absolute',
              left: pin1.position,
              top: 0,
              bottom: 0,
              width: 2,
              background: 'var(--et-color-semantic-primary-foreground)',
              zIndex: 9995,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: -16,
                left: -16,
                fontSize: 10,
                fontWeight: 700,
                color: '#fff',
                background: 'var(--et-color-semantic-primary-foreground)',
                padding: '1px 4px',
                borderRadius: 3,
                whiteSpace: 'nowrap',
              }}
            >
              {pin1.time.format(ONLY_TIME_FORMAT)}
            </div>
          </div>
        )}

        {rangeSelection && pin2 && (
          <div
            style={{
              position: 'absolute',
              left: pin2.position,
              top: 0,
              bottom: 0,
              width: 2,
              background: 'var(--et-color-semantic-primary-foreground)',
              zIndex: 9995,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: -16,
                left: -16,
                fontSize: 10,
                fontWeight: 700,
                color: '#fff',
                background: 'var(--et-color-semantic-primary-foreground)',
                padding: '1px 4px',
                borderRadius: 3,
                whiteSpace: 'nowrap',
              }}
            >
              {pin2.time.format(ONLY_TIME_FORMAT)}
            </div>
          </div>
        )}

        {hoverPosition != null && (
          <div
            style={{
              position: 'absolute',
              left: hoverPosition,
              top: 0,
              bottom: 0,
              width: 1,
              background: 'rgba(74, 96, 255, 0.5)',
              zIndex: 9994,
              pointerEvents: 'none',
            }}
          >
            {hoverTime && (
              <div
                style={{
                  position: 'absolute',
                  bottom: -16,
                  ...(hoverPosition > scene.width - 40
                    ? { right: 4 }
                    : { left: 4 }),
                  fontSize: 10,
                  fontWeight: 600,
                  color: 'var(--et-color-semantic-primary-foreground)',
                  background:
                    'var(--activity-range-info-bg, rgba(255,255,255,0.9))',
                  padding: '0 3px',
                  borderRadius: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                {hoverTime.format(ONLY_TIME_FORMAT)}
              </div>
            )}
          </div>
        )}

        {rangeSelection && sortedPins && (
          <div
            style={{
              position: 'absolute',
              left: sortedPins.start.position,
              top: 0,
              bottom: 0,
              width: sortedPins.end.position - sortedPins.start.position,
              background: 'rgba(74, 96, 255, 0.08)',
              border: '1px dashed rgba(74, 96, 255, 0.4)',
              zIndex: 9993,
              pointerEvents: 'none',
            }}
          />
        )}

        {rangeSelection && sortedPins && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: leftOverlayWidth,
              background:
                'var(--activity-range-overlay, rgba(255, 255, 255, 0.80))',
              zIndex: 9992,
              pointerEvents: 'none',
            }}
          />
        )}

        {rangeSelection && sortedPins && (
          <div
            style={{
              position: 'absolute',
              left: rightOverlayLeft,
              top: 0,
              bottom: 0,
              width: rightOverlayWidth,
              background:
                'var(--activity-range-overlay, rgba(255, 255, 255, 0.80))',
              zIndex: 9992,
              pointerEvents: 'none',
            }}
          />
        )}

        {rangeSelection &&
          sortedPins &&
          selectionSummary &&
          chartContainerRef.current &&
          (() => {
            const containerRect =
              chartContainerRef.current.getBoundingClientRect()
            const midPoint =
              (sortedPins.start.position + sortedPins.end.position) / 2
            const tooltipWidth = 460
            let leftPos = containerRect.left + midPoint - tooltipWidth / 2
            if (leftPos < 8) leftPos = 8
            if (leftPos + tooltipWidth > window.innerWidth - 8)
              leftPos = window.innerWidth - tooltipWidth - 8
            const topPos = containerRect.top - 46

            return ReactDOM.createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: topPos,
                  left: leftPos,
                  zIndex: 990,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  pointerEvents: 'auto',
                }}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    clearPins()
                  }}
                  className="tw-bg-primary"
                  style={{
                    border: 'none',
                    borderRadius: 6,
                    padding: '4px 8px',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#fff',
                    flexShrink: 0,
                  }}
                >
                  <FaIcon icon={faClose} />
                </button>

                <div
                  style={{
                    background: 'var(--activity-range-info-bg, #fff)',
                    border: '2px solid var(--activity-range-info-bg, #fff)',
                    borderRadius: 2,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    padding: '6px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 12,
                    color: 'var(--activity-range-info-text, #333)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      borderRight:
                        '1px solid var(--activity-range-info-border, #ddd)',
                      paddingRight: 10,
                      lineHeight: '16px',
                    }}
                  >
                    <div
                      style={{
                        color: 'var(--et-color-semantic-primary-foreground)',
                      }}
                    >
                      {selectionSummary.period}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--activity-range-info-muted, #888)',
                      }}
                    >
                      Σ {selectionSummary.totalSelected}
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'center',
                      flexWrap: 'nowrap',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <img
                        src="/assets/icons_nucleo/icons/activity/driving-black.svg"
                        width={13}
                        height={13}
                      />
                      {selectionSummary[ActivityType.DRIVING]}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <img
                        src="/assets/icons_nucleo/icons/activity/work-black.svg"
                        width={13}
                        height={13}
                      />
                      {selectionSummary[ActivityType.WORK]}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <img
                        src="/assets/icons_nucleo/icons/activity/availability-black.svg"
                        width={13}
                        height={13}
                      />
                      {selectionSummary[ActivityType.AVAILABILITY]}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <img
                        src="/assets/icons_nucleo/icons/activity/rest-black.svg"
                        width={13}
                        height={13}
                      />
                      {selectionSummary[ActivityType.REST]}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <img
                        src="/assets/icons_nucleo/icons/activity/unknown.svg"
                        width={13}
                        height={13}
                      />
                      {selectionSummary[ActivityType.UNKNOWN]}
                    </span>
                  </div>
                </div>
              </div>,
              document.body,
            )
          })()}
      </div>
    </div>
  )
}

RangeSelectionTimelineWrapper.displayName = 'RangeSelectionTimelineWrapper'
